import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Link2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { messaggioErrore } from "@/lib/soldi";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import StatusPill from "@/components/common/StatusPill";

/**
 * Il collegamento a Fatture in Cloud, nelle Impostazioni. Lo accende e lo collega
 * solo il titolare; l'amministrazione ne vede lo stato.
 *
 * I token non passano mai di qui: stanno sul server (fic_connessione, colonne
 * che nessun utente legge). Questa scheda legge solo lo stato e l'avviso.
 *
 * Si registra in ImpostazioniPage.js come le altre schede (TabsTrigger +
 * TabsContent, value "fatture-in-cloud"), visibile a titolare e amministrazione.
 */

const STATI = {
  non_collegato: { label: "Non collegato", pill: "neutro" },
  collegato: { label: "Collegato", pill: "ok" },
  rinnovo_fallito: { label: "Rinnovo non riuscito", pill: "errore" },
  errore: { label: "Errore", pill: "errore" },
};

export default function FattureInCloudTab() {
  const { profile } = useAuth();
  const titolare = profile?.ruolo === "titolare";
  const [st, setSt] = useState(null);
  const [errore, setErrore] = useState(null);
  const [busy, setBusy] = useState(false);

  const carica = useCallback(() => {
    api
      .get("/fic/stato")
      .then((r) => {
        setSt(r.data);
        setErrore(null);
      })
      .catch((e) => setErrore(messaggioErrore(e)));
  }, []);
  useEffect(carica, [carica]);

  const interruttore = async (attivo) => {
    setBusy(true);
    try {
      const r = await api.post("/fic/interruttore", { attivo });
      setSt(r.data);
      toast.success(attivo ? "Collegamento acceso" : "Collegamento spento");
    } catch (e) {
      toast.error(messaggioErrore(e));
    } finally {
      setBusy(false);
    }
  };

  const collega = async () => {
    setBusy(true);
    try {
      const r = await api.post("/fic/collega");
      window.location.assign(r.data.url);
    } catch (e) {
      toast.error(messaggioErrore(e));
      setBusy(false);
    }
  };

  if (errore) {
    return (
      <div role="alert" className="rounded-xl border border-danger/30 bg-danger-subtle px-card py-group text-sm text-danger-foreground">
        {errore}
      </div>
    );
  }
  if (!st) return <Skeleton className="h-40 rounded-xl" />;

  const s = STATI[st.stato] || STATI.non_collegato;
  const accendibile = st.credenziali && st.stato === "collegato";
  return (
    <Card className="p-card" data-testid="impostazioni-fic">
      <div className="flex flex-wrap items-start justify-between gap-inside">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-foreground">Fatture in Cloud</h2>
          <p className="mt-micro max-w-xl text-sm text-muted-foreground">
            Le fatture d'acconto e di saldo preparate dal piano di pagamento partono da qui verso il gestionale del commercialista.
            Il preventivo resta nella piattaforma.
          </p>
        </div>
        <StatusPill stato={s.pill}>{s.label}</StatusPill>
      </div>

      {st.avviso ? (
        <div role="alert" className="mt-group flex gap-inside rounded-lg border border-danger/30 bg-danger-subtle px-group py-inside text-sm text-danger-foreground" data-testid="fic-avviso">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p>{st.avviso}</p>
        </div>
      ) : null}

      <div className="mt-card grid gap-group">
        <div className="flex items-center justify-between gap-group">
          <div className="min-w-0">
            <Label htmlFor="fic-attivo" className="text-sm font-medium text-foreground">
              Manda le fatture a Fatture in Cloud
            </Label>
            <p className="mt-micro text-xs text-muted-foreground">
              {st.attivo
                ? "Acceso: dalla scheda Fatture il bottone «Invia» crea la fattura lì."
                : "Spento: le fatture si preparano qui e si emettono a mano."}
            </p>
          </div>
          <Switch
            id="fic-attivo"
            checked={Boolean(st.attivo)}
            disabled={!titolare || busy || (!st.attivo && !accendibile)}
            onCheckedChange={interruttore}
            data-testid="fic-interruttore"
            className="relative after:absolute after:inset-x-0 after:-inset-y-2.5 after:content-['']"
          />
        </div>

        {!st.credenziali ? (
          <p className={cn("rounded-lg bg-muted px-group py-inside text-sm text-muted-foreground")} data-testid="fic-credenziali">
            Mancano le credenziali dell'applicazione Fatture in Cloud sul server. Si creano dal conto di Interni D'Autore su
            Fatture in Cloud (applicazione privata) e si mettono nella configurazione del backend: poi da qui si collega.
          </p>
        ) : null}

        {titolare ? (
          <Button
            variant="outline"
            className="h-11 sm:h-9 justify-self-start"
            onClick={collega}
            disabled={busy || !st.credenziali}
            data-testid="fic-collega"
          >
            <Link2 />
            {st.stato === "collegato" ? "Ricollega" : "Collega Fatture in Cloud"}
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">Collegamento e interruttore li gestisce il titolare.</p>
        )}
      </div>
    </Card>
  );
}
